import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Briefcase, Building2, Clock, Wallet, Users2,
  CalendarClock, Mail, Phone, Globe, Hash,
  Instagram, Facebook, Linkedin, Twitter, Link2, ArrowUpRight,
} from 'lucide-react';
import { useLocale } from '../i18n/LocaleProvider';
import {
  pick, employmentTypeLabel, remotePolicyLabel,
} from '../i18n/pick';
import { getJob } from '../api/jobs';
import { mediaUrl, orgLogo, formatDate, orgAccent } from '../lib/media';
import { renderBody } from '../lib/markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Chip from '../components/Chip';
import Seo from '../components/Seo';
import MailMenu from '../components/MailMenu';
import { Loading } from '../components/States';
import NotFound from './NotFound';

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  pinterest: Link2,
};

function resolveApply(job, locale, t) {
  const title = pick(job, locale, 'title');
  if (job.apply_url) {
    return { type: 'url', href: job.apply_url, label: t.applyOnline };
  }
  const email = job.apply_email || job.organization?.contact_email;
  if (email) {
    return {
      type: 'email',
      email,
      subject: t.applySubject(title),
      body: t.applyBody(title),
      label: t.applyByEmail,
    };
  }
  return null;
}

const APPLY_BTN =
  'inline-flex w-full items-center justify-center gap-2 rounded-full bg-greenDeep px-6 py-3 font-num text-xs font-medium uppercase tracking-wider text-cream shadow-card transition-colors hover:bg-gold hover:text-greenDeep';

function ApplyButton({ apply, className = '' }) {
  if (!apply) return null;
  if (apply.type === 'url') {
    return (
      <a
        href={apply.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${APPLY_BTN} ${className}`}
      >
        {apply.label}
        <ArrowUpRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <div className={className}>
      <MailMenu
        email={apply.email}
        subject={apply.subject}
        body={apply.body}
        label={apply.label}
        className={APPLY_BTN}
        block
      />
    </div>
  );
}

function AboutOrg({ org }) {
  const { t, locale } = useLocale();
  const logo = orgLogo(org);
  const tagline = pick(org, locale, 'tagline');
  const about = pick(org, locale, 'about');
  const socials = org.socials || {};
  const socialEntries = Object.entries(socials).filter(([, url]) => !!url);

  return (
    <div className="rounded-2xl border border-sand bg-cream p-6">
      <div className="flex items-center gap-3">
        {logo && (
          <img src={logo} alt={org.name} className="h-12 w-12 rounded-lg border border-sand bg-white object-contain p-1" />
        )}
        <div>
          <p className="eyebrow">{t.aboutOrg(org.name)}</p>
          <p className="font-display text-lg text-greenDeep">{org.name}</p>
        </div>
      </div>

      {tagline && <p className="mt-3 text-sm italic text-ink/75">{tagline}</p>}
      {about && <div className="markdown mt-3 text-sm">{renderBody(about)}</div>}

      <div className="mt-4 space-y-2 text-sm">
        {org.address && (
          <a
            href={org.map_url || undefined}
            target={org.map_url ? '_blank' : undefined}
            rel="noopener noreferrer"
            className="flex items-start gap-2 text-ink/80 hover:text-greenDeep"
          >
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.75} />
            <span>{org.address}</span>
          </a>
        )}
        {org.website && (
          <a
            href={org.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-ink/80 hover:text-greenDeep"
          >
            <Globe className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.75} />
            <span>{t.visitWebsite}</span>
          </a>
        )}
      </div>

      {socialEntries.length > 0 && (
        <div className="mt-4 flex gap-2">
          {socialEntries.map(([key, url]) => {
            const Icon = SOCIAL_ICONS[key] || Link2;
            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={key}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-sand text-greenDeep transition-colors hover:border-gold hover:text-gold"
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ContactPerson({ person }) {
  const { t } = useLocale();
  if (!person || !person.full_name) return null;
  const photo = mediaUrl(person.photo);
  return (
    <div className="rounded-2xl border border-sand bg-cream p-6">
      <p className="eyebrow">{t.contact}</p>
      <div className="mt-3 flex items-center gap-3">
        {photo ? (
          <img src={photo} alt={person.full_name} className="h-12 w-12 rounded-full object-cover" />
        ) : (
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-greenDeep font-num text-sm text-cream">
            {person.full_name.split(' ').map((s) => s[0]).slice(0, 2).join('')}
          </span>
        )}
        <div>
          <p className="font-medium text-greenDeep">{person.full_name}</p>
          {person.poste && <p className="text-sm text-muted-foreground">{person.poste}</p>}
        </div>
      </div>
      <div className="mt-3 space-y-1.5 text-sm">
        {person.email && (
          <a href={`mailto:${person.email}`} className="flex items-center gap-2 text-ink/80 hover:text-greenDeep">
            <Mail className="h-4 w-4 text-gold" strokeWidth={1.75} /> {person.email}
          </a>
        )}
        {person.telephone && (
          <a href={`tel:${person.telephone}`} className="flex items-center gap-2 text-ink/80 hover:text-greenDeep">
            <Phone className="h-4 w-4 text-gold" strokeWidth={1.75} /> {person.telephone}
          </a>
        )}
      </div>
    </div>
  );
}

export default function JobDetail() {
  const { slug } = useParams();
  const { t, locale } = useLocale();
  const [status, setStatus] = useState('loading'); // loading | ready | notfound | error
  const [job, setJob] = useState(null);

  const load = useCallback(() => {
    setStatus('loading');
    getJob(slug)
      .then((data) => {
        setJob(data);
        setStatus('ready');
      })
      .catch((e) => {
        console.error('Failed to load job:', e);
        setStatus(e.status === 404 ? 'notfound' : 'error');
      });
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, [load]);

  if (status === 'notfound') return <NotFound />;

  const title = job ? pick(job, locale, 'title') : '';
  const teaser = job ? pick(job, locale, 'teaser') : '';
  const org = job?.organization || {};
  const apply = job ? resolveApply(job, locale, t) : null;
  const cover = mediaUrl(job?.cover);
  const accent = orgAccent(org.slug);

  const description = job ? pick(job, locale, 'description_markdown') : '';
  const profile = job ? pick(job, locale, 'profile_markdown') : '';
  const offer = job ? pick(job, locale, 'offer_markdown') : '';

  const location = job ? pick(job, locale, 'location') : '';
  const schedule = job ? pick(job, locale, 'schedule_label') : '';
  const salary = job ? pick(job, locale, 'salary_range') : '';
  const department = job ? pick(job, locale, 'department') : '';
  const tags = Array.isArray(job?.tags) ? job.tags : [];

  return (
    <div className="flex min-h-screen flex-col">
      <Seo
        title={title ? `${title} · ${org.name || t.brandSuffix}` : t.metaTitleHome}
        description={teaser}
        image={cover}
        locale={locale}
      />
      <Header />

      <main className="flex-1">
        <div className="container pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-num text-xs uppercase tracking-wider text-greenDeep/70 transition-colors hover:text-gold"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t.backToList}
          </Link>
        </div>

        {status === 'loading' && <Loading />}
        {status === 'error' && (
          <div className="container py-24 text-center">
            <h2 className="font-display text-2xl text-greenDeep">{t.errorTitle}</h2>
            <p className="mt-2 text-muted-foreground">{t.errorBody}</p>
            <button
              onClick={load}
              className="mt-4 rounded-full bg-greenDeep px-5 py-2 font-num text-xs uppercase tracking-wider text-cream hover:bg-green"
            >
              {t.retry}
            </button>
          </div>
        )}

        {status === 'ready' && job && (
          <div className="container py-8">
            {cover && (
              <div className="mb-8 overflow-hidden rounded-2xl border border-sand">
                <img src={cover} alt={title} className="h-56 w-full object-cover sm:h-72" />
              </div>
            )}

            <div className="grid gap-10 lg:grid-cols-[1fr_340px]">
              {/* Main column */}
              <article className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full" style={{ backgroundColor: accent }} />
                  {org.name && (
                    <Link
                      to={`/?org=${org.slug}`}
                      className="font-num text-xs uppercase tracking-wider text-greenDeep/70 hover:text-gold"
                    >
                      {org.name}
                    </Link>
                  )}
                </div>

                <h1 className="mt-2 font-display text-3xl font-semibold leading-[1.12] text-greenDeep sm:text-4xl">
                  {title}
                </h1>

                {teaser && <p className="mt-3 max-w-2xl text-lg text-ink/75">{teaser}</p>}

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {location && <Chip icon={MapPin}>{location}</Chip>}
                  <Chip icon={Briefcase} tone="accent">
                    {employmentTypeLabel(job.employment_type, locale)}
                  </Chip>
                  {job.remote_policy && <Chip icon={Building2}>{remotePolicyLabel(job.remote_policy, locale)}</Chip>}
                  {schedule && <Chip icon={Clock}>{schedule}</Chip>}
                  {salary && <Chip icon={Wallet}>{salary}</Chip>}
                  {department && <Chip icon={Users2}>{department}</Chip>}
                  {job.openings > 1 && <Chip icon={Users2}>{t.openingsSeats(job.openings)}</Chip>}
                  {job.closes_at && <Chip icon={CalendarClock}>{t.closesOn(formatDate(job.closes_at, locale))}</Chip>}
                </div>

                {/* Mobile apply CTA */}
                <div className="mt-6 lg:hidden">
                  <ApplyButton apply={apply} />
                </div>

                {description && <div className="markdown mt-8">{renderBody(description)}</div>}

                {profile && (
                  <section className="mt-10">
                    <h2 className="font-display text-2xl text-greenDeep">{t.sectionProfile}</h2>
                    <div className="markdown mt-3">{renderBody(profile)}</div>
                  </section>
                )}

                {offer && (
                  <section className="mt-10">
                    <h2 className="font-display text-2xl text-greenDeep">{t.sectionOffer}</h2>
                    <div className="markdown mt-3">{renderBody(offer)}</div>
                  </section>
                )}

                {(tags.length > 0 || job.reference_code) && (
                  <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-sand pt-6">
                    {tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-secondary px-3 py-1 font-num text-[11px] uppercase tracking-wide text-greenDeep/70">
                        #{tag}
                      </span>
                    ))}
                    {job.reference_code && (
                      <span className="ml-auto inline-flex items-center gap-1 font-num text-[11px] uppercase tracking-wider text-muted-foreground">
                        <Hash className="h-3 w-3" /> {t.reference}: {job.reference_code}
                      </span>
                    )}
                  </div>
                )}
              </article>

              {/* Sidebar */}
              <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
                <div className="hidden rounded-2xl border border-sand bg-cream p-6 lg:block">
                  <p className="font-display text-lg text-greenDeep">{t.apply}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{title}</p>
                  <ApplyButton apply={apply} className="mt-4" />
                </div>

                <AboutOrg org={org} />
                <ContactPerson person={job.contact_person} />
              </aside>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
